/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { cloneDeep, get, isEmpty, isObject } from 'lodash';

// Components
import CustomActionButton from 'shared/ui/components/form-components/CustomActionButton.vue';
import ExpandableContent from 'shared/ui/components/ExpandableContent.vue';
import LoaderButton from 'shared/ui/components/form-components/LoaderButton.vue';
import MetaFieldset from 'shared/ui/components/form-components/MetaFieldset.vue';

/**
 * This page is used to handle dynamicly created forms
 */
const __vue_script__ = {
    name: 'MetaForm',

    components: { CustomActionButton, ExpandableContent, LoaderButton, MetaFieldset },

    props: {
        value: {
            type: Object,
            default: () => ({})
        },

        fields: {
            type: Array,
            default: () => ([])
        },

        event: {
            type: Object,
            default: () => ({})
        },

        currentController: {
            type: Object,
            default: () => ({})
        },

        fpType: {
            type: String,
            default: ''
        },

        showToolbar: {
            type: Boolean,
            default: false
        },

        /**
         * This is the trigger for the saving. Changing this value to a non
         * null value will trigger the saving routine.
         */
        saveHook: {
            type: Number,
            default: null
        },

        /**
         * There are form controls that stores some values in the browser store,
         * in order to avoid name clashing we use a `context`.
         * In most cases this coincide with the event ID.
         *
         * An example where we use the context in order to store data is the color
         * picker, where we want to store a palette based on the event ID.
         */
        context: {
            type: String,
            default: null
        },

        /**
         * This component is smart enough to determine if we're editing a record
         * or creating a new one. But in order to have a better control we can use
         * this flag to overwrite the default behaviour.
         */
        edit: {
            type: Boolean,
            default: false
        },

        /**
         * This flag is used to determine if we should redirect the user to the
         * listing page after the create operation.
         */
        redirectOnSave: {
            type: Boolean,
            default: true
        },

        /**
         * Flag for preventing the component to split the page into two panels.
         */
        singlePanel: {
            type: Boolean,
            default: false
        }
    },

    data() {
        const buttons = this.currentController.routeOptions?.customActionButtons || [];

        return {
            customActions: Object.keys(buttons).map(k => ({ _name: k, ...buttons[k] })),
            editing: this.edit || this.value.hasOwnProperty('_id'),
            innerValue: {},
            saving: false,
            valid: false,
            originalValue: {},
            settings: {},
            metadataModule: this.$services.blueprint?.getModuleByName('metadata'),
            disabled: false
        };
    },

    computed: {
        /** @returns {object[]} */
        fieldsSorted() {
            const visibleFields = this.fields.filter(field => this.isVisible(field));

            if (this.singlePanel) {
                return [this.$utils.controls.sortAndGroupFields(visibleFields, this.$i18n.t('metadata_editor.no_category'), this.editing)];
            }

            const fieldsLeft = this.$utils.controls.fieldsByPlacement(visibleFields, 'left');
            const fieldsRight = this.$utils.controls.fieldsByPlacement(visibleFields, 'right');

            const sortedLeft = this.$utils.controls.sortAndGroupFields(fieldsLeft, this.$i18n.t('metadata_editor.no_category'), this.editing);
            const sortedRight = this.$utils.controls.sortAndGroupFields(fieldsRight, this.$i18n.t('metadata_editor.no_category'), this.editing);

            return [sortedLeft, sortedRight];
        },

        /** @returns {boolean} */
        collapsiblePrimaryFieldsets() {
            return Object.keys(this.primaryFieldsSorted).length > 1;
        },

        /** @returns {boolean} */
        collapsibleSecondaryFieldsets() {
            return Object.keys(this.secondaryFieldsSorted).length > 1;
        },

        /** @returns {boolean} */
        showMetadataButton() {
            if (!this.metadataModule || !this.$services.user.hasPermission(this.metadataModule.permission, { org_id: this.event?.ownerId })) {
                return false;
            }
            return get(this, 'event._id') && !isEmpty(this.fpType);
        }
    },

    watch: {
        value: {
            handler(value) {
                this.innerValue = cloneDeep(value);
                this.originalValue = cloneDeep(value);
                this.disabled = Boolean(this.innerValue.fp_readonly);
            },
            immediate: true
        },

        saveHook(value) {
            if (value) {
                this.save();
            }
        }
    },

    async created() {
        try {
            this.settings = await this.$services.settings.getSettings(this.event._id);
        } catch (error) {
            console.error('[MetaForm] Error fetching settings', error.message);
            this.settings = {};
        }
    },

    methods: {
        /**
         * Check if a field is visible or not
         * @param field
         */
        isVisible(field) {
            if (field.hidden || this.$services.user.isInvitedByLink(this.event) && field.hideForInvited) {
                return false;
            }
            const settingsCond = field.conditions?.settings;
            if (Array.isArray(settingsCond)) {
                let visible = false;
                for (const { path, value } of settingsCond) {
                    visible = get(this.settings, path) === value;
                    if (!visible) {
                        break;
                    }
                }
                return visible;
            }
            return true;
        },

        /**
         * Returns the href for the metadata editor.
         *
         * @returns {string} The href for the metadata editor.
         */
        getMetadataEditorHref() {
            return this.$utils.modules.getRouteHref(this.metadataModule, 'default', {
                type: this.fpType,
                eventId: this.event._id
            }, (routes) => routes.find(r => r.match(/:type/)));
        },

        /**
         * Resets the form model
         */
        reset() {
            this.innerValue = {};
            for (const fd of this.fields) {
                this.innerValue[fd.field] = null;
            }
        },

        /**
         * Checks form validity
         *
         * @returns {Promise.<Boolean>} the form validity
         */
        async validate() {
            const validForm = !this.$refs.form ? true : await this.$refs.form.validate();

            this.valid = validForm;

            if (this.valid) {
                this.$emit('valid', this.innerValue);
            }

            console.info('[MetaForm] Validity:', this.valid, this.innerValue);

            return this.valid;
        },

        /**
         * Saves the model remotely
         *
         * @returns {Promise<Object>} the server response
         */
        async save() {
            if (!await this.validate()) {
                this.$emit('error');
                return;
            }

            console.debug('[MetaForm] %s the document', this.editing ? 'Updating' : 'Creating');

            try {
                this.saving = true;

                if (this.editing) {
                    await this.update();
                } else {
                    await this.create();
                }

                this.$emit('saved', this.innerValue);

                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.t('generic_form.messages.created').toString(),
                });

            } catch (error) {
                let { field, detail } = this.$utils.controls.parseServerError(error);

                if (this.$te(detail)) {
                    detail = this.$t(detail);
                }

                if (field) {
                    this.$refs.form.setErrors({ [field]: [detail] });
                }

                this.$emit('error', error, field, detail);

                this.$notify({
                    group: 'flashes',
                    text: detail,
                    type: 'error'
                });

            } finally {
                this.saving = false;
            }
        },

        /**
         * Retrieves the saving options for the form.
         *
         * @returns {Object} The saving options object.
         */
        async getSavingOptions() {
            const { _id, node } = this.event;
            const metadataForType = await this.$services.metadata.getMetadataForTypeAsArray(_id, this.fpType);
            const fieldsToFillIfEmpty = this.$utils.metadata.findFieldsWithFlag(metadataForType, 'generate_value_if_empty');
            const options = { fieldsToFillIfEmpty, include_upserted_doc_ids: true };

            return { eid: _id, node, options };
        },

        /**
         * Creates a new record
         *
         * @returns {Promise<Object>} the server response
         *
         * @throws {Error}
         */
        async create() {
            const { eid, node, options } = await this.getSavingOptions();
            const { data } = await this.$services.documents.createDocs(eid, node, this.fpType, this.sanitize(this.innerValue), options);

            this.$set(this.innerValue, '_id', data.upsertedDocIds[0]);

            await this.postProcess(data);

            if (await this.$services.backlinks.isBacklinksSearchAvailable(eid)) {
                await this.$services.backlinks.triggerAndWaitForBacklinksRefresh(eid);
            }

            if (this.redirectOnSave || this.currentController.routeOptions?.afterSaveRedirectRoute) {
                const route = this.currentController.routeOptions?.afterSaveRedirectRoute || this.$services.blueprint.getFpTypeListingPath(eid, this.fpType);
                const normalizedRoute = this.$utils.modules.normalizeRoute(route);
                const path = this.$utils.url.interpolateUrl(normalizedRoute, {
                    eventId: eid,
                    fpType: this.fpType,
                    id: data.upsertedDocIds[0],
                    _id: data.upsertedDocIds[0]
                });

                this.$router.push(path);
            }
        },

        /**
         * Updates an existing record
         *
         * @returns {Promise<Object>} the server response
         *
         * @throws {Error}
         */
        async update() {
            const { eid, node, options } = await this.getSavingOptions();

            let ignoreProtectedFields;

            if (this.$services.documents.doProtectedFieldsDiffer(this.originalValue, this.innerValue)) {
                ignoreProtectedFields = options.ignoreProtected = confirm(
                    'You have made changes in protected fields. ' +
                    'These data were set by the user. Overwrite these?'
                );
            }

            const { data } = await this.$services.documents.updateDoc(eid, node, this.fpType, this.sanitize(this.innerValue), options);

            if (typeof this.innerValue.afterSave === 'function') {
                this.innerValue.afterSave();
            }

            await this.postProcess(data);

            // take care of our view of protected field value state
            if (this.originalValue.fp_protected) {
                this.originalValue.fp_protected.forEach(field => {
                    if (ignoreProtectedFields) { // update original model with new protected field values
                        this.originalValue[field] = this.innerValue[field];
                    } else { // restore values back to their originals
                        this.innerValue[field] = this.originalValue[field];
                    }
                });
            }
        },

        /**
         * This method is used to call the `onAfterModelSaveAppScript` app script
         */
        async postProcess(data) {
            const postScript = get(this, 'currentController.routeOptions.onAfterModelSaveAppScript');
            if (postScript) {
                console.warn('[MetaForm] Calling post process script on the FE is deprecated. Please refactor the package!', postScript);
                const params = {
                    model_id: !isEmpty(data.upsertedDocIds) ? data.upsertedDocIds[0] : this.innerValue._id,
                    changed: !isEmpty(data.upsertedDocIds)
                };

                return await this.$services.appScripts.callAppScript(this.event._id, postScript, params);
            }
        },

        /**
         * This handler is used to update the revision of the
         * current model
         *
         * @param {string} rev the new rev
         */
        revUpdate(rev) {
            console.info('[MetaForm] New rev update received', rev);
            if (this.innerValue._rev !== rev) {
                this.$set(this.innerValue, '_rev', rev);
            }
        },

        /**
         * This handler is used to add a new behaviour to the
         * current model.
         *
         * Currently supported: `afterSave` => function
         *
         * @param {{'afterSave': function}} behaviours an array of new behaviours
         */
        addBehaviours(behaviours) {
            console.info('[MetaForm] New behaviour received', behaviours);
            for (const behaviour of Object.keys(behaviours)) {
                if (typeof behaviours[behaviour] === 'function') {
                    this.innerValue[behaviour] = behaviours[behaviour];
                }
            }
        },

        /**
         * Sanitizes the input data.
         *
         * @param {Object} form the form to sanitize
         *
         * @returns {Object} a copy of the inner value without offending properties
         */
        sanitize(form) {
            const formCopy = cloneDeep(form);

            if (isObject(formCopy)) {
                for (const [property, value] of Object.entries(formCopy)) {
                    if (property.startsWith('$$') || property.startsWith('__')) {
                        delete formCopy[property];
                    } else if (Array.isArray(value)) {
                        formCopy[property] = value.map(this.sanitize);
                    }
                }
            }

            return formCopy;
        },

        /**
         * Handles the input for multi-value fields.
         * This is a special case for Angular JS components
         * that can emit more than just a single field value.
         *
         * @param {string} field - The field name.
         * @param {any} value - The input value.
         */
        handleMultiInput(field, value) {
            this.$set(this.innerValue, field, value);
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('validation-observer',{ref:"form",staticClass:"meta-form",attrs:{"tag":"form"},on:{"submit":function($event){$event.preventDefault();return _vm.save()}}},[_c('div',{staticClass:"inner-form",class:{ 'panels panels-b2': !_vm.singlePanel }},[_vm._l((_vm.fieldsSorted),function(fieldSorted,panelIndex){return [(Object.keys(fieldSorted).length)?_c('div',{staticClass:"panel"},[(panelIndex === 0)?_vm._t("primary-panel-top"):_vm._e(),(panelIndex === 1)?_vm._t("secondary-panel-top"):_vm._e(),(Object.keys(fieldSorted).length > 1)?[_vm._l((fieldSorted),function(fieldset,legend){return [(legend.startsWith('_no_category_'))?[_c('meta-fieldset',{key:legend,attrs:{"disabled":_vm.disabled,"event":_vm.event,"fieldset":fieldset,"settings":_vm.settings,"context":_vm.context,"edit":_vm.editing},on:{"multi-input":_vm.handleMultiInput,"behaviour":_vm.addBehaviours,"rev":_vm.revUpdate},model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}})]:[_c('expandable-content',{key:legend,staticClass:"fieldset",attrs:{"clickable-header":true,"is-open":true,"limit-height":false,"unlimited-height":true}},[_c('div',{attrs:{"slot":"header"},slot:"header"},[_c('h5',[_vm._v(_vm._s(legend))])]),_c('meta-fieldset',{attrs:{"disabled":_vm.disabled,"event":_vm.event,"fieldset":fieldset,"settings":_vm.settings,"context":_vm.context,"edit":_vm.editing},on:{"multi-input":_vm.handleMultiInput,"behaviour":_vm.addBehaviours,"rev":_vm.revUpdate},model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}})],1)]]})]:_vm._l((fieldSorted),function(fieldset,legend){return _c('meta-fieldset',{key:legend,attrs:{"disabled":_vm.disabled,"event":_vm.event,"fieldset":fieldset,"settings":_vm.settings,"context":_vm.context,"edit":_vm.editing},on:{"multi-input":_vm.handleMultiInput,"behaviour":_vm.addBehaviours,"rev":_vm.revUpdate},model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}})})],2):_vm._e()]})],2),(_vm.showToolbar)?_c('secondary-toolbar',{scopedSlots:_vm._u([{key:"contextual",fn:function(ref){return [(_vm.showMetadataButton)?_c('a',{staticClass:"btn btn-icon",attrs:{"href":_vm.getMetadataEditorHref(),"target":"_blank","rel":"noopener noreferrer"}},[_c('i',{staticClass:"icon-cog"}),_vm._v(" "+_vm._s(_vm.$t('generic_form.types.meta_edit')))]):_vm._e(),_vm._l((_vm.customActions),function(action){return _c('custom-action-button',{attrs:{"event":_vm.event,"action":action,"context":_vm.innerValue},on:{"change":function($event){return _vm.$emit('saved')}}})}),_vm._t("contextual")]}}],null,true)},[_c('loader-button',{staticClass:"btn btn-primary",attrs:{"disabled":_vm.disabled || _vm.saving,"status":_vm.saving},on:{"click":function($event){$event.preventDefault();return _vm.save()}}},[_vm._v(_vm._s(_vm.$t('general.save')))])],1):_vm._e()],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "MetaForm.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__